import { useCallback, useEffect, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useSnackbar } from 'notistack'

import { handleDrop } from './helpers'

/**
 * This hook automatically revokes object URLs when they change or when the component unmounts.
 * This prevents memory leaks from accumulating blob URLs created with URL.createObjectURL().
 * @param url - The URL to clean up. If it starts with 'blob:', it will be revoked on change or unmount.
 * @example
 * const [devicePhoto, setDevicePhoto] = useState<string | null>(null)
 * useObjectUrlCleanup(devicePhoto)
 * // The object URL will be automatically revoked when devicePhoto changes or component unmounts
 */
const useObjectUrlCleanup = (url: string | null) => {
  useEffect(() => {
    if (url && url.startsWith('blob:')) {
      return () => URL.revokeObjectURL(url)
    }
    return undefined
  }, [url])
}

/**
 * Custom hook that provides drag-and-drop functionality for device photo uploads.
 * Supports image file dropping, clipboard pasting, and automatic object URL cleanup.
 * Handles file validation and error notifications through snackbar integration.
 *
 * Features:
 * - Drag and drop image files (.jpeg, .jpg, .png, .gif)
 * - Clipboard paste support for images
 * - Automatic object URL cleanup to prevent memory leaks
 * - Photo modal state management
 * - File validation with user feedback
 * - Single file upload limitation
 *
 * @returns {Object} Hook return object
 * @returns {boolean} photoModalOpen - Whether the photo modal is currently open
 * @returns {function} setPhotoModalOpen - Function to control photo modal visibility
 * @returns {string | null} uploadedPhoto - URL of the uploaded photo
 * @returns {function} setUploadedPhoto - Function to set the uploaded photo URL
 * @returns {string | null} devicePhoto - URL of the device photo
 * @returns {function} setDevicePhoto - Function to set the device photo URL
 * @returns {function} getRootProps - Dropzone root props for drag-and-drop area
 * @returns {function} getInputProps - Dropzone input props for file input
 * @returns {boolean} isDragActive - Whether files are currently being dragged over the dropzone
 * @returns {function} openFilePicker - Function to programmatically open the file picker
 */
const usePhotoDropzone = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [photoModalOpen, setPhotoModalOpen] = useState<boolean>(false)
  const [uploadedPhoto, setUploadedPhoto] = useState<string | null>(null)
  const [devicePhoto, setDevicePhoto] = useState<string | null>(null)

  // Cleanup object URL when component unmounts or devicePhoto changes
  useObjectUrlCleanup(devicePhoto)

  // Handle file drop
  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
      handleDrop({ enqueueSnackbar, acceptedFiles, fileRejections, setUploadedPhoto, setPhotoModalOpen })
    },
    [enqueueSnackbar]
  )

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    inputRef,
    open: openFilePicker,
  } = useDropzone({
    onDrop,
    accept: globalThis.isJest ? {} : { 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] },
    maxFiles: 1,
    noClick: true,
  })

  // Handle pasting images from clipboard
  useEffect(() => {
    const handlePasteFile = async (event: ClipboardEvent) => {
      if (event.clipboardData?.files && inputRef.current) {
        inputRef.current.files = event.clipboardData.files
        inputRef.current.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }
    window.addEventListener('paste', handlePasteFile)
    return () => {
      window.removeEventListener('paste', handlePasteFile)
    }
  }, [inputRef])

  return {
    photoModalOpen,
    setPhotoModalOpen,
    uploadedPhoto,
    setUploadedPhoto,
    devicePhoto,
    setDevicePhoto,
    getRootProps,
    getInputProps,
    isDragActive,
    openFilePicker,
  }
}

export default usePhotoDropzone
